import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

function downloadDetailsFile(path, filename) {
  const link = document.createElement('a');
  link.href = path;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function FundTypeahead({ token, onSelect, placeholder, clearAfterSelect = false, categoryFilter = null }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    try {
      const params = { q };
      if (categoryFilter) params.category = categoryFilter;
      const res = await axios.get('/api/mfd/search', { headers: { Authorization: `Bearer ${token}` }, params });
      const funds = res.data.funds || res.data || [];
      setResults(funds.slice(0, 30));
      setOpen(true);
    } catch { setResults([]); }
  }, [token, categoryFilter]);

  const handleChange = (e) => {
    const q = e.target.value; setQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 300);
  };

  const handleSelect = (fund) => {
    onSelect(fund);
    if (clearAfterSelect) setQuery(''); else setQuery(fund.name || fund.scheme_name || fund);
    setOpen(false); setResults([]);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input value={query} onChange={handleChange} placeholder={placeholder || 'Type fund name...'}
        style={s.typeaheadInput} onFocus={() => results.length > 0 && setOpen(true)} />
      {open && results.length > 0 && (
        <div style={s.dropdown}>
          {results.map((f, i) => (
            <div key={i} style={s.dropdownItem} onMouseDown={() => handleSelect(f)}>
              {f.name || f.scheme_name || f}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

async function downloadFile(endpoint, params, headers, filename, format) {
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const hasQuery = endpoint.includes('?');
  const reqUrl = hasQuery ? `${endpoint}&format=${format}` : endpoint;
  const res = await axios.get(reqUrl, { headers, params: hasQuery ? {} : { ...params, format }, responseType: 'blob' });
  const objUrl = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = objUrl;
  link.setAttribute('download', `${filename}.${ext}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objUrl);
}

export default function MFDDashboard() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get('/api/mfd/watchlist', { headers }).then(res => {
      const funds = res.data.funds || [];
      const codes = res.data.scheme_codes || [];
      if (funds.length > 0) setWatchlist(funds.map(f => ({ id: f.scheme_code, name: f.scheme_name })));
      else if (codes.length > 0) setWatchlist(codes.map(c => ({ id: c, name: c })));
    }).catch(() => {});
  }, [token]);

  const [msg, setMsg] = useState({ text: '', error: false });
  const [loading, setLoading] = useState('');
  const [watchlist, setWatchlist] = useState([]);
  const [compFunds, setCompFunds] = useState([null, null]);
  const [compMode, setCompMode] = useState('free');
  const [compFund1Category, setCompFund1Category] = useState(null);
  const [basket, setBasket] = useState('Moderate Equity Growth Plan');
  const [sipAmount, setSipAmount] = useState(10000);
  const [fundsPerCat, setFundsPerCat] = useState(2);
  const [deepFund, setDeepFund] = useState(null);

  // Change password modal state
  const [showCpModal, setShowCpModal] = useState(false);
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpShowCurrent, setCpShowCurrent] = useState(false);
  const [cpShowNew, setCpShowNew] = useState(false);
  const [cpShowConfirm, setCpShowConfirm] = useState(false);
  const [cpMsg, setCpMsg] = useState({ text: '', error: false });
  const [cpLoading, setCpLoading] = useState(false);

  const showMsg = (text, error = false) => { setMsg({ text, error }); setTimeout(() => setMsg({ text: '', error: false }), 4000); };

  const handleChangePassword = async () => {
    setCpMsg({ text: '', error: false });
    if (!cpCurrent || !cpNew || !cpConfirm) { setCpMsg({ text: 'All fields are required.', error: true }); return; }
    if (cpNew.length < 8) { setCpMsg({ text: 'New password must be at least 8 characters.', error: true }); return; }
    if (cpNew !== cpConfirm) { setCpMsg({ text: 'New passwords do not match.', error: true }); return; }
    setCpLoading(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, { current_password: cpCurrent, new_password: cpNew }, { headers });
      setCpMsg({ text: 'Password changed successfully.', error: false });
      setCpCurrent(''); setCpNew(''); setCpConfirm('');
    } catch (e) {
      setCpMsg({ text: e.response?.data?.error || 'Failed to change password.', error: true });
    } finally { setCpLoading(false); }
  };

  const closeCpModal = () => { setShowCpModal(false); setCpMsg({ text: '', error: false }); setCpCurrent(''); setCpNew(''); setCpConfirm(''); };

  const saveWatchlist = (list) => { setWatchlist(list); };
  const addToWatchlist = async (fund) => {
    const name = fund.name || fund.scheme_name || fund;
    const id = fund.id || fund.scheme_code || name;
    if (watchlist.length >= 50) { showMsg('Watchlist is full (max 50 funds)', true); return; }
    if (watchlist.find(f => (f.id || f.name) === id)) { showMsg('Fund already in watchlist', true); return; }
    try {
      await axios.post('/api/mfd/watchlist/add', { scheme_code: id }, { headers });
      setWatchlist(prev => [...prev, { id, name }]);
    } catch { showMsg('Failed to add fund', true); }
  };
  const removeFromWatchlist = async (id) => {
    try {
      await axios.post('/api/mfd/watchlist/remove', { scheme_code: id }, { headers });
      setWatchlist(prev => prev.filter(f => (f.id || f.name) !== id));
    } catch { showMsg('Failed to remove fund', true); }
  };
  const setCompFund = (index, fund) => {
    const updated = [...compFunds];
    updated[index] = fund ? { id: fund.id || fund.scheme_code || fund.name || fund, name: fund.name || fund.scheme_name || fund, category_code: fund.category_code || null } : null;
    setCompFunds(updated);
    if (index === 0) setCompFund1Category(fund ? (fund.category_code || null) : null);
  };
  const removeCompFund = (index) => {
    const updated = compFunds.filter((_, i) => i !== index);
    setCompFunds(updated.length < 2 ? [...updated, ...Array(2 - updated.length).fill(null)] : updated);
    if (index === 0) setCompFund1Category(null);
  };

  const handleDownload = async (reportKey, format) => {
    const key = `${reportKey}-${format}`;
    setLoading(key);
    try {
      switch (reportKey) {
        case 'performance': {
          if (watchlist.length === 0) { showMsg('Watchlist is empty. Add funds first.', true); break; }
          await downloadFile('/api/mfd/report/watchlist', {}, headers, 'Performance_Report', format);
          showMsg('Performance Report downloaded'); break;
        }
        case 'comparison': {
          const chosen = compFunds.filter(Boolean);
          if (chosen.length < 2) { showMsg('Please select at least 2 funds to compare', true); break; }
          const compQuery = chosen.map(f => `scheme_code=${encodeURIComponent(f.id)}`).join('&');
          await downloadFile(`/api/mfd/report/comparison?${compQuery}`, {}, headers, 'Fund_Comparison', format);
          showMsg('Fund Comparison downloaded'); break;
        }
        case 'sip': {
          await downloadFile('/api/mfd/report/sip-builder', { risk_profile: basket, sip_amount: sipAmount, funds_per_cat: fundsPerCat }, headers, `SIP_Builder_${basket.replace(/ /g, '_')}`, format);
          showMsg('SIP Builder report downloaded'); break;
        }
        case 'deepdive': {
          if (!deepFund) { showMsg('Please select a fund first', true); break; }
          await downloadFile('/api/mfd/report/factsheet', { scheme_code: deepFund.id }, headers, `Deep_Dive_${deepFund.name.replace(/ /g, '_')}`, format);
          showMsg('Deep Dive report downloaded'); break;
        }
        case 'scorebreakdown': {
          await downloadFile('/api/mfd/report/score-breakdown', {}, headers, 'ScoreBreakdown', format);
          showMsg('Score Breakdown downloaded'); break;
        }
        case 'debtplan': {
          await downloadFile('/api/mfd/report/debt-growth-plan', {}, headers, 'Debt_Growth_Plan', format);
          showMsg('Debt Growth Plan downloaded'); break;
        }
        default: break;
      }
    } catch (e) {
      showMsg(`Download failed: ${e.response?.data?.error || e.response?.data?.message || e.message}`, true);
    } finally { setLoading(''); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const isLoading = (key, fmt) => loading === `${key}-${fmt}`;

  return (
    <div style={s.page}>
      {showCpModal && (
        <div style={s.modalOverlay} onClick={closeCpModal}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>Change Password</span>
              <button onClick={closeCpModal} style={s.modalClose}>✕</button>
            </div>
            {cpMsg.text && <div style={{ ...s.msgBox, ...(cpMsg.error ? s.msgError : s.msgSuccess), marginBottom: '16px' }}>{cpMsg.text}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { label: 'Current Password', val: cpCurrent, set: setCpCurrent, show: cpShowCurrent, setShow: setCpShowCurrent },
                { label: 'New Password', val: cpNew, set: setCpNew, show: cpShowNew, setShow: setCpShowNew },
                { label: 'Confirm New Password', val: cpConfirm, set: setCpConfirm, show: cpShowConfirm, setShow: setCpShowConfirm },
              ].map(({ label, val, set, show, setShow }) => (
                <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{label}</label>
                  <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                    <input type={show ? 'text' : 'password'} value={val} onChange={e => set(e.target.value)}
                      style={{ flex: 1, padding: '10px 12px', border: 'none', fontSize: '14px', outline: 'none' }} placeholder={label} />
                    <button type="button" onClick={() => setShow(v => !v)}
                      style={{ background: 'none', border: 'none', padding: '0 12px', cursor: 'pointer', fontSize: '15px' }}>
                      {show ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={handleChangePassword} disabled={cpLoading}
              style={{ marginTop: '20px', width: '100%', background: '#1565C0', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
              {cpLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>
      )}
      <div style={s.navbar}>
        <span style={s.brand}>Fund Intelligence Platform</span>
        <div style={s.navRight}>
          <span style={s.navUser}>{user?.name || user?.email} (MFD)</span>
          <button onClick={() => setShowCpModal(true)} style={s.cpNavBtn}>Change Password</button>
          <button onClick={handleLogout} style={s.logoutBtn}>Logout</button>
        </div>
      </div>
      <div style={s.container}>
        <h2 style={s.pageTitle}>MFD Dashboard</h2>
        {msg.text && (
          <div style={{ ...s.msgBox, ...(msg.error ? s.msgError : s.msgSuccess) }}>
            {msg.error ? 'Warning: ' : 'OK: '}{msg.text}
          </div>
        )}
        <div style={s.grid}>

          <div style={s.reportCard}>
            <div style={s.reportHeader}>
              <div><div style={s.reportName}>Category Report</div><div style={s.reportDesc}>Top Regular Plan funds by SEBI category</div></div>
              <span style={s.reportNum}>01</span>
            </div>
            <div style={s.btnRow}>
              <button style={s.xlsBtn} onClick={() => navigate('/mfd/category-report')}>Open Report</button>
              <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R01_Category_Report_Details.pdf', 'R01_Category_Report_Details.pdf')}>Details</button>
            </div>
          </div>

          <div style={s.reportCard}>
            <div style={s.reportHeader}>
              <div><div style={s.reportName}>Performance Report</div><div style={s.reportDesc}>Report on your saved watchlist funds</div></div>
              <span style={s.reportNum}>02</span>
            </div>
            <div style={s.inputZone}>
              <div style={s.inputZoneLabel}>Watchlist <span style={s.badge}>{watchlist.length} / 50</span></div>
              <FundTypeahead token={token} onSelect={addToWatchlist} placeholder="Type fund name to search and add..." clearAfterSelect={true} />
              {watchlist.length > 0 && (
                <div style={s.chipCloud}>
                  {watchlist.map(f => (
                    <span key={f.id} style={s.chip}>{f.name}<span style={s.chipX} onClick={() => removeFromWatchlist(f.id)}>x</span></span>
                  ))}
                </div>
              )}
              {watchlist.length === 0 && <div style={s.emptyNote}>No funds in watchlist yet. Search above to add.</div>}
            </div>
            <div style={s.btnRow}>
              <button style={s.xlsBtn} disabled={isLoading('performance', 'excel')} onClick={() => handleDownload('performance', 'excel')}>{isLoading('performance', 'excel') ? 'Loading...' : 'Excel'}</button>
              <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R02_Watchlist_Details.pdf', 'R02_Watchlist_Details.pdf')}>Details</button>
            </div>
          </div>

          <div style={s.reportCard}>
            <div style={s.reportHeader}>
              <div><div style={s.reportName}>Fund Comparison</div><div style={s.reportDesc}>Detailed scorecard - 2 funds side by side</div></div>
              <span style={s.reportNum}>03</span>
            </div>
            <div style={s.inputZone}>
              <div style={s.inputZoneLabel}>Comparison mode</div>
              <div style={{display:'flex', gap:'8px', marginBottom:'12px'}}>
                <button style={compMode === 'free' ? {...s.modeBtn, ...s.modeBtnActive} : s.modeBtn} onClick={() => { setCompMode('free'); setCompFund1Category(null); setCompFunds([null, null]); }}>Free Search</button>
                <button style={compMode === 'samecat' ? {...s.modeBtn, ...s.modeBtnActive} : s.modeBtn} onClick={() => { setCompMode('samecat'); setCompFund1Category(null); setCompFunds([null, null]); }}>Same Category</button>
              </div>
              {compMode === 'samecat' && <div style={s.modeNote}>Fund 2 will be filtered to the same SEBI category as Fund 1</div>}
              <div style={s.inputZoneLabel}>Select funds to compare <span style={s.badge}>{compFunds.filter(Boolean).length} / 2</span></div>
              <div style={s.fundSlots}>
                {compFunds.map((fund, i) => (
                  <div key={i} style={s.fundSlotRow}>
                    <span style={s.slotNum}>{i + 1}</span>
                    {fund ? (
                      <div style={s.fundSlotFilled}><span style={s.fundSlotName}>{fund.name}</span><span style={s.chipX} onClick={() => removeCompFund(i)}>x</span></div>
                    ) : (
                      <FundTypeahead token={token} onSelect={(f) => setCompFund(i, f)}
                        placeholder={i === 1 && compMode === 'samecat' && compFund1Category ? 'Search in same category...' : 'Search fund...'}
                        clearAfterSelect={false} categoryFilter={i === 1 && compMode === 'samecat' && compFund1Category ? compFund1Category : null} />
                    )}
                  </div>
                ))}
              </div>
              {compMode === 'samecat' && compFunds[0] && !compFund1Category && <div style={{...s.modeNote, color:'#CC0000', marginTop:'6px'}}>Category not detected for Fund 1 - same-category filter unavailable</div>}
            </div>
            <div style={s.btnRow}>
              <button style={s.pdfBtn} disabled={isLoading('comparison', 'pdf')} onClick={() => handleDownload('comparison', 'pdf')}>{isLoading('comparison', 'pdf') ? 'Loading...' : 'PDF'}</button>
              <button style={s.xlsBtn} disabled={isLoading('comparison', 'excel')} onClick={() => handleDownload('comparison', 'excel')}>{isLoading('comparison', 'excel') ? 'Loading...' : 'Excel'}</button>
              <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R03_Fund_Comparison_Details.pdf', 'R03_Fund_Comparison_Details.pdf')}>Details</button>
            </div>
          </div>

          <div style={s.reportCard}>
            <div style={s.reportHeader}>
              <div><div style={s.reportName}>SIP Builder</div><div style={s.reportDesc}>Auto-suggested basket with % allocation - totals 100%</div></div>
              <span style={s.reportNum}>04</span>
            </div>
            <div style={s.inputZone}>
              <div style={s.inputZoneLabel}>Select basket type</div>
              <div style={s.basketBtns}>
                {['Conservative Equity Growth Plan', 'Moderate Equity Growth Plan', 'Aggressive Equity Growth Plan'].map(b => (
                  <button key={b} style={basket === b ? { ...s.basketBtn, ...s.basketBtnActive[b] } : s.basketBtn} onClick={() => setBasket(b)}>{b}</button>
                ))}
              </div>
            </div>
            <div style={s.btnRow}>
              <button style={s.pdfBtn} disabled={isLoading('sip', 'pdf')} onClick={() => handleDownload('sip', 'pdf')}>{isLoading('sip', 'pdf') ? 'Loading...' : 'PDF'}</button>
              <button style={s.xlsBtn} disabled={isLoading('sip', 'excel')} onClick={() => handleDownload('sip', 'excel')}>{isLoading('sip', 'excel') ? 'Loading...' : 'Excel'}</button>
              <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R04_SIP_Builder_Details.pdf', 'R04_SIP_Builder_Details.pdf')}>Details</button>
            </div>
          </div>

          <div style={s.reportCard}>
            <div style={s.reportHeader}>
              <div><div style={s.reportName}>Deep Dive</div><div style={s.reportDesc}>Full analysis - NAV history, returns, risk, fund manager, scoring with comments</div></div>
              <span style={s.reportNum}>05</span>
            </div>
            <div style={s.inputZone}>
              <div style={s.inputZoneLabel}>Select fund</div>
              {!deepFund ? (
                <FundTypeahead token={token} onSelect={(f) => setDeepFund({ id: f.id || f.scheme_code || f.name || f, name: f.name || f.scheme_name || f })} placeholder="Type fund name..." clearAfterSelect={false} />
              ) : (
                <div style={s.selectedFundRow}><span style={s.selectedFundName}>{deepFund.name}</span><span style={s.chipX} onClick={() => setDeepFund(null)}>x</span></div>
              )}
            </div>
            <div style={s.btnRow}>
              <button style={s.pdfBtn} disabled={isLoading('deepdive', 'pdf')} onClick={() => handleDownload('deepdive', 'pdf')}>{isLoading('deepdive', 'pdf') ? 'Loading...' : 'PDF'}</button>
              <button style={s.xlsBtn} disabled={isLoading('deepdive', 'excel')} onClick={() => handleDownload('deepdive', 'excel')}>{isLoading('deepdive', 'excel') ? 'Loading...' : 'Excel'}</button>
              <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R05_Deep_Dive_Details.pdf', 'R05_Deep_Dive_Details.pdf')}>Details</button>
            </div>
          </div>

          <div style={s.reportCard}>
            <div style={s.reportHeader}>
              <div><div style={s.reportName}>Portfolio Audit</div><div style={s.reportDesc}>Upload client MF statement - fund scoring + alternatives</div></div>
              <span style={s.reportNum}>06</span>
            </div>
            <div style={s.btnRow}>
              <button style={s.xlsBtn} onClick={() => navigate('/mfd/portfolio-audit')}>Open Report</button>
              <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R06_Portfolio_Audit_Details.pdf', 'R06_Portfolio_Audit_Details.pdf')}>Details</button>
            </div>
          </div>

          <div style={s.reportCard}>
            <div style={s.reportHeader}>
              <div><div style={s.reportName}>Score Breakdown</div><div style={s.reportDesc}>Per-parameter percentile scores and weighted contributions for all funds</div></div>
              <span style={s.reportNum}>07</span>
            </div>
            <div style={s.btnRow}>
              <button style={s.xlsBtn} disabled={isLoading('scorebreakdown', 'excel')} onClick={() => handleDownload('scorebreakdown', 'excel')}>{isLoading('scorebreakdown', 'excel') ? 'Loading...' : 'Excel'}</button>
              <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R07_Score_Breakdown_Details.pdf', 'R07_Score_Breakdown_Details.pdf')}>Details</button>
            </div>
          </div>

          <div style={s.reportCard}>
            <div style={s.reportHeader}>
              <div><div style={s.reportName}>Debt Growth Plan</div><div style={s.reportDesc}>Lumpsum debt portfolio - fixed allocation across 5 debt categories</div></div>
              <span style={s.reportNum}>08</span>
            </div>
            <div style={s.btnRow}>
              <button style={s.pdfBtn} disabled={isLoading('debtplan', 'pdf')} onClick={() => handleDownload('debtplan', 'pdf')}>{isLoading('debtplan', 'pdf') ? 'Loading...' : 'PDF'}</button>
              <button style={s.xlsBtn} disabled={isLoading('debtplan', 'excel')} onClick={() => handleDownload('debtplan', 'excel')}>{isLoading('debtplan', 'excel') ? 'Loading...' : 'Excel'}</button>
              <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/R08_Debt_Growth_Plan_Details.pdf', 'R08_Debt_Growth_Plan_Details.pdf')}>Details</button>
            </div>
          </div>

          <div style={s.reportCard}>
            <div style={s.reportHeader}>
              <div><div style={s.reportName}>Platform Master Guide</div><div style={s.reportDesc}>How FIP scores every fund on the platform — for your reference</div></div>
            </div>
            <div style={s.btnRow}>
              <button style={s.detailsBtn} onClick={() => downloadDetailsFile('/details/MASTER_How_FIP_Scores_Funds_Details.pdf', 'MASTER_How_FIP_Scores_Funds_Details.pdf')}>Download Master Details</button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Segoe UI', sans-serif" },
  navbar: { background: '#1565C0', color: '#fff', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  brand: { fontWeight: '700', fontSize: '18px' },
  navRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  navUser: { fontSize: '14px' },
  cpNavBtn: { background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  logoutBtn: { background: '#CC0000', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: '600' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: '#fff', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1a1a2e' },
  modalClose: { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666', lineHeight: 1 },
  container: { maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' },
  pageTitle: { color: '#1565C0', marginBottom: '20px', fontSize: '22px', fontWeight: '700' },
  msgBox: { borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '14px' },
  msgSuccess: { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
  msgError: { background: '#ffebee', color: '#CC0000', border: '1px solid #ffcdd2' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' },
  reportCard: { background: '#fff', borderRadius: '12px', padding: '22px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', gap: '14px' },
  reportHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  reportNum: { fontSize: '11px', fontWeight: '700', color: '#1565C0', background: '#E3F0FF', padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' },
  reportName: { fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  reportDesc: { fontSize: '13px', color: '#666', lineHeight: '1.5' },
  inputZone: { background: '#f8faff', border: '1.5px solid #e0eaff', borderRadius: '10px', padding: '14px' },
  inputZoneLabel: { fontSize: '12px', fontWeight: '700', color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' },
  badge: { background: '#1565C0', color: '#fff', fontSize: '11px', padding: '2px 8px', borderRadius: '20px', fontWeight: '600' },
  clearLink: { fontSize: '12px', color: '#CC0000', cursor: 'pointer', marginTop: '8px', display: 'inline-block' },
  typeaheadInput: { width: '100%', padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #ddd', borderRadius: '8px', zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto' },
  dropdownItem: { padding: '10px 14px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', color: '#333' },
  chipCloud: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' },
  chip: { fontSize: '12px', padding: '4px 8px 4px 10px', borderRadius: '20px', background: '#E3F0FF', color: '#1565C0', border: '1px solid #b3cff5', display: 'flex', alignItems: 'center', gap: '6px' },
  chipX: { color: '#CC0000', cursor: 'pointer', fontSize: '15px', lineHeight: 1, fontWeight: '700' },
  emptyNote: { fontSize: '12px', color: '#aaa', marginTop: '8px', fontStyle: 'italic' },
  fundSlots: { display: 'flex', flexDirection: 'column', gap: '8px' },
  fundSlotRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  slotNum: { fontSize: '11px', color: '#999', minWidth: '16px', textAlign: 'center', fontWeight: '700' },
  fundSlotFilled: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#E3F0FF', border: '1px solid #b3cff5', borderRadius: '8px', padding: '8px 12px' },
  fundSlotName: { fontSize: '12px', color: '#1565C0', flex: 1 },
  modeBtn: { flex: 1, padding: '8px 6px', fontSize: '12px', border: '1.5px solid #ddd', borderRadius: '8px', background: '#fff', color: '#555', cursor: 'pointer', fontWeight: '600', textAlign: 'center' },
  modeBtnActive: { background: '#E3F0FF', color: '#1565C0', border: '1.5px solid #1565C0' },
  modeNote: { fontSize: '11px', color: '#1565C0', background: '#E3F0FF', border: '1px solid #b3cff5', borderRadius: '6px', padding: '6px 10px', marginBottom: '10px' },
  basketBtns: { display: 'flex', gap: '8px', marginBottom: '12px' },
  basketBtn: { flex: 1, padding: '9px 6px', fontSize: '13px', border: '1.5px solid #ddd', borderRadius: '8px', background: '#fff', color: '#555', cursor: 'pointer', fontWeight: '600', textAlign: 'center' },
  basketBtnActive: {
    'Conservative Equity Growth Plan': { background: '#e8f5e9', color: '#2e7d32', border: '1.5px solid #66bb6a' },
    'Moderate Equity Growth Plan':     { background: '#fff8e1', color: '#f57f17', border: '1.5px solid #ffca28' },
    'Aggressive Equity Growth Plan':   { background: '#ffebee', color: '#CC0000', border: '1.5px solid #ef9a9a' },
  },
  selectedFundRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#E3F0FF', border: '1px solid #b3cff5', borderRadius: '8px', padding: '10px 14px' },
  selectedFundName: { fontSize: '13px', color: '#1565C0', flex: 1 },
  uploadZone: { border: '2px dashed #b3cff5', borderRadius: '10px', padding: '20px', textAlign: 'center', cursor: 'pointer', background: '#fff' },
  uploadIcon: { fontSize: '24px', color: '#1565C0', marginBottom: '6px' },
  uploadText: { fontSize: '13px', color: '#555', marginBottom: '4px' },
  uploadFileName: { fontSize: '13px', color: '#1565C0', fontWeight: '600', marginBottom: '4px' },
  uploadNote: { fontSize: '11px', color: '#aaa' },
  btnRow: { display: 'flex', gap: '10px', marginTop: 'auto' },
  pdfBtn: { flex: 1, background: '#CC0000', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  xlsBtn: { flex: 1, background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  detailsBtn: { flex: 1, background: '#616161', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
};
